
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, MapPin, Mail, Instagram, Facebook, Youtube, ChevronDown, ArrowUp, Star } from 'lucide-react';
import { COMPANY_NAME, PHONE_NUMBER, WHATSAPP_NUMBER, ADDRESS, EMAIL, SERVICES, GOOGLE_MAPS_LINK, GOOGLE_REVIEWS_LINK, YOUTUBE_LINK, INSTAGRAM_LINK, FACEBOOK_LINK } from '../constants.ts';
import Logo from './Logo.tsx';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    // Lock body scroll when mobile menu is open
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
      setShowScrollTop(window.scrollY > 600);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isActive = (path: string) => location.pathname === path
    ? "text-gold-400 font-bold transition-all duration-300"
    : "text-gray-100 hover:text-gold-300 font-medium transition-all duration-300";

  return (
    <div className="flex flex-col min-h-screen font-sans bg-cream-50 text-gray-800 selection:bg-gold-500 selection:text-white">
      {/* Top Bar - Ultra Responsive */}
      <div className="bg-black text-gray-300 py-2 px-3 md:px-4 text-[10px] xs:text-xs md:text-sm border-b border-gray-900 relative z-[101]">
        <div className="container mx-auto flex justify-between items-center max-w-[1800px]">
          <div className="flex items-center space-x-3 md:space-x-8">
            <a href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`} className="flex items-center hover:text-gold-400 transition-colors duration-300 group">
              <Phone className="w-3 h-3 md:w-3.5 md:h-3.5 mr-1.5 md:mr-2 text-gold-500 group-hover:scale-110 transition-transform" />
              <span className="inline font-bold">{PHONE_NUMBER}</span>
            </a>
            <a href={`mailto:${EMAIL}`} className="hidden sm:flex items-center hover:text-gold-400 transition-colors duration-300 group">
              <Mail className="w-3 h-3 md:w-3.5 md:h-3.5 mr-1.5 md:mr-2 text-gold-500 group-hover:scale-110 transition-transform" /> {EMAIL}
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <span className="hidden lg:inline text-gray-500 font-light tracking-widest text-[10px] uppercase">Crafting Excellence Since 2012</span>
            <div className="flex items-center space-x-3 md:space-x-4">
              <a href={FACEBOOK_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-gold-500 transition-all hover:scale-110" aria-label="Facebook"><Facebook className="w-3.5 h-3.5 md:w-4 md:h-4" /></a>
              <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-gold-500 transition-all hover:scale-110" aria-label="Instagram"><Instagram className="w-3.5 h-3.5 md:w-4 md:h-4" /></a>
              <a href={YOUTUBE_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-gold-500 transition-all hover:scale-110" aria-label="YouTube"><Youtube className="w-3.5 h-3.5 md:w-4 md:h-4" /></a>
              <a href={GOOGLE_REVIEWS_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-gold-500 transition-all hover:scale-110" aria-label="Review us"><Star className="w-3.5 h-3.5 md:w-4 md:h-4" /></a>
            </div>
          </div>
        </div>
      </div>

      {/* Navbar - Sticky & Responsive */}
      <header
        className={`sticky top-0 z-[100] w-full transition-all duration-300 ease-in-out border-b ${scrolled || isMenuOpen
          ? 'bg-maroon-950/95 backdrop-blur-md py-2 md:py-3 shadow-2xl border-white/5'
          : 'bg-maroon-950 py-3 md:py-5 border-transparent'
          }`}
      >
        <div className="container mx-auto px-4 lg:px-8 max-w-[1800px]">
          <div className="flex justify-between items-center">
            <Link to="/" onClick={closeMenu} className="block hover:opacity-90 transition-all duration-300 z-50">
              <Logo className={`transition-all duration-300 ${scrolled ? "w-10 h-10 md:w-12 md:h-12" : "w-12 h-12 md:w-16 md:h-16"}`} />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex space-x-6 lg:space-x-12 3xl:space-x-20 items-center">
              <Link to="/" className={`${isActive('/')} text-xs lg:text-sm uppercase tracking-[0.2em]`}>Home</Link>
              <Link to="/about" className={`${isActive('/about')} text-xs lg:text-sm uppercase tracking-[0.2em]`}>About</Link>

              <div className="relative group">
                <button className="flex items-center text-gray-100 hover:text-gold-400 py-4 text-xs lg:text-sm uppercase tracking-[0.2em] font-medium transition-colors focus:outline-none">
                  Services <ChevronDown className="w-4 h-4 ml-1.5 opacity-50 group-hover:rotate-180 transition-transform duration-500" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 mt-0 w-72 bg-white shadow-3xl rounded-xl overflow-hidden invisible group-hover:visible opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500 border-t-[6px] border-gold-500 z-50">
                  <div className="p-2">
                    {SERVICES.map(service => (
                      <Link
                        key={service.id}
                        to={`/services/${service.id}`}
                        className="block px-6 py-4 text-sm text-gray-700 hover:bg-maroon-50 hover:text-maroon-900 transition-all duration-200 rounded-lg"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                  <Link to="/gallery" className="block px-6 py-4 text-xs font-black text-center uppercase tracking-widest text-white bg-maroon-900 hover:bg-maroon-800 transition-colors">
                    View Portfolio
                  </Link>
                </div>
              </div>

              <Link to="/gallery" className={`${isActive('/gallery')} text-xs lg:text-sm uppercase tracking-[0.2em]`}>Gallery</Link>
              <Link to="/contact" className={`${isActive('/contact')} text-xs lg:text-sm uppercase tracking-[0.2em]`}>Contact</Link>

              <Link
                to="/contact"
                className={`bg-gold-500 hover:bg-gold-400 text-maroon-950 font-black rounded-lg transition-all duration-300 shadow-xl hover:shadow-gold-500/30 uppercase tracking-[0.15em] transform hover:-translate-y-1 active:scale-95 ${scrolled ? 'py-2.5 px-6 text-xs' : 'py-3.5 px-8 text-sm'}`}
              >
                Get Quote
              </Link>
            </nav>

            {/* Mobile Menu Button - High Z-index to stay above overlay */}
            <button
              className="md:hidden text-white hover:text-gold-500 transition-all p-2 rounded-lg bg-white/10 active:scale-90 touch-manipulation z-50 relative"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close Menu" : "Open Menu"}
            >
              {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Nav Overlay */}
      <div
        className={`md:hidden fixed inset-0 bg-maroon-950 z-[90] transition-all duration-300 ease-in-out ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
        style={{ paddingTop: '80px' }} // Spacing for the sticky header
      >
        <div className="flex flex-col h-full overflow-y-auto pb-32">
          <div className="p-6 space-y-6">
            <Link to="/" onClick={closeMenu} className="block text-white text-3xl font-serif border-b border-white/5 pb-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>Home</Link>
            <Link to="/about" onClick={closeMenu} className="block text-white text-3xl font-serif border-b border-white/5 pb-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>About Us</Link>

            <div className="space-y-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <span className="text-gold-500 text-xs uppercase tracking-[0.3em] font-black block">Our Services</span>
              <div className="grid grid-cols-1 gap-3 pl-4">
                {SERVICES.map(service => (
                  <Link
                    key={service.id}
                    to={`/services/${service.id}`}
                    onClick={closeMenu}
                    className="text-gray-300 hover:text-white text-lg transition-colors py-2 block"
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/gallery" onClick={closeMenu} className="block text-white text-3xl font-serif border-b border-white/5 pb-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>Gallery</Link>
            <Link to="/contact" onClick={closeMenu} className="block text-white text-3xl font-serif border-b border-white/5 pb-4 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>Contact</Link>

            <div className="pt-4 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <Link to="/contact" onClick={closeMenu} className="block w-full bg-gold-500 text-maroon-950 text-center py-4 rounded-xl font-black text-lg shadow-2xl active:scale-95 transition-transform">
                Request Free Quote
              </Link>
            </div>

            <div className="flex justify-center gap-8 pt-6 animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
              <a href={FACEBOOK_LINK} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gold-500" aria-label="Facebook"><Facebook className="w-6 h-6" /></a>
              <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gold-500" aria-label="Instagram"><Instagram className="w-6 h-6" /></a>
              <a href={YOUTUBE_LINK} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gold-500" aria-label="YouTube"><Youtube className="w-6 h-6" /></a>
              <a href={GOOGLE_REVIEWS_LINK} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gold-500" aria-label="Review us"><Star className="w-6 h-6" /></a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Sticky Action Center */}
      <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[55] flex flex-col space-y-3 md:space-y-4 pointer-events-none">
        <button
          onClick={scrollToTop}
          className={`pointer-events-auto bg-maroon-900 text-gold-500 p-3 md:p-4 rounded-full md:rounded-2xl shadow-2xl hover:bg-maroon-800 transition-all transform duration-500 border border-white/10 ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        <a
          href={GOOGLE_REVIEWS_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="pointer-events-auto bg-white text-maroon-900 p-3 md:p-4 rounded-full md:rounded-2xl shadow-2xl hover:scale-110 active:scale-95 transition-all group flex items-center justify-center border border-gold-100"
          aria-label="Review us on Google"
          title="Review us on Google"
        >
          <Star className="w-5 h-5 md:w-6 md:h-6 text-gold-500 fill-gold-500 group-hover:animate-spin" />
        </a>

        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="pointer-events-auto bg-[#25D366] text-white p-3 md:p-4 rounded-full md:rounded-2xl shadow-2xl hover:scale-110 active:scale-95 transition-all group flex items-center justify-center animate-pulse-slow"
          aria-label="WhatsApp"
        >
          <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
        </a>

        <a
          href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}
          className="pointer-events-auto bg-blue-600 text-white p-3 md:p-4 rounded-full md:rounded-2xl shadow-2xl hover:scale-110 active:scale-95 transition-all group flex items-center justify-center"
          aria-label="Call Now"
        >
          <Phone className="w-5 h-5 md:w-6 md:h-6" />
        </a>
      </div>

      {/* Premium Footer */}
      <footer className="bg-maroon-950 text-gray-200 pt-16 md:pt-24 pb-28 md:pb-12 border-t-[8px] border-gold-600 relative overflow-hidden group">
        {/* Background Base */}
        <div className="absolute inset-0 bg-maroon-950"></div>

        {/* Requested Subtle Animated Radial Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Main central pulse - slow breathing effect */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[radial-gradient(circle_at_center,_rgba(197,160,89,0.07)_0%,_transparent_60%)] animate-pulse-slow"></div>

          {/* Color cycling gradient overlay for subtle hue shifts */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(128,0,32,0.15),_transparent_70%)] animate-color-cycle mix-blend-overlay"></div>
        </div>

        {/* Existing Blobs for texture and movement */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-maroon-800/30 rounded-full mix-blend-screen filter blur-[100px] opacity-30 animate-blob pointer-events-none"></div>
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-gold-600/10 rounded-full mix-blend-screen filter blur-[100px] opacity-30 animate-blob pointer-events-none" style={{ animationDelay: '2s' }}></div>
        <div className="absolute -bottom-32 left-1/3 w-[600px] h-[600px] bg-maroon-900/40 rounded-full mix-blend-screen filter blur-[120px] opacity-40 animate-blob pointer-events-none" style={{ animationDelay: '4s' }}></div>

        <div className="container mx-auto px-6 lg:px-12 max-w-[1800px] relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-12">
            <div className="lg:col-span-1 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="transform hover:scale-105 transition-transform duration-300">
                <Logo className="w-20 h-20 mb-6 md:mb-8" />
              </div>
              <p className="text-gray-100 leading-relaxed font-bold text-sm md:text-base mb-8">
                Midnapore's premier award-winning event planners. We blend traditional Bengali artistry with modern sophistication to create breathtaking experiences.
              </p>
              <div className="flex space-x-5">
                <a href={FACEBOOK_LINK} target="_blank" rel="noopener noreferrer" className="w-10 h-10 md:w-12 md:h-12 border-2 border-white/20 rounded-xl flex items-center justify-center hover:bg-gold-500 hover:text-maroon-950 hover:border-gold-500 transition-all transform hover:scale-110" aria-label="Facebook">
                  <Facebook className="w-4 h-4 md:w-5 md:h-5 font-bold" />
                </a>
                <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" className="w-10 h-10 md:w-12 md:h-12 border-2 border-white/20 rounded-xl flex items-center justify-center hover:bg-gold-500 hover:text-maroon-950 hover:border-gold-500 transition-all transform hover:scale-110" aria-label="Instagram">
                  <Instagram className="w-4 h-4 md:w-5 md:h-5 font-bold" />
                </a>
                <a href={YOUTUBE_LINK} target="_blank" rel="noopener noreferrer" className="w-10 h-10 md:w-12 md:h-12 border-2 border-white/20 rounded-xl flex items-center justify-center hover:bg-gold-500 hover:text-maroon-950 hover:border-gold-500 transition-all transform hover:scale-110" aria-label="YouTube">
                  <Youtube className="w-4 h-4 md:w-5 md:h-5 font-bold" />
                </a>
                <a href={GOOGLE_REVIEWS_LINK} target="_blank" rel="noopener noreferrer" className="w-10 h-10 md:w-12 md:h-12 border-2 border-white/20 rounded-xl flex items-center justify-center hover:bg-gold-500 hover:text-maroon-950 hover:border-gold-500 transition-all transform hover:scale-110" aria-label="Google Reviews">
                  <Star className="w-4 h-4 md:w-5 md:h-5 font-bold" />
                </a>
              </div>
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h4 className="text-gold-500 font-serif font-black text-lg md:text-xl mb-6 md:mb-8 flex items-center">
                <span className="w-8 h-[3px] bg-gold-600 mr-4"></span>
                Services
              </h4>
              <ul className="grid grid-cols-1 gap-3 md:gap-4">
                {SERVICES.map(s => (
                  <li key={s.id}>
                    <Link to={`/services/${s.id}`} className="text-gray-300 hover:text-white hover:translate-x-2 transition-all inline-block font-bold text-sm md:text-base">
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <h4 className="text-gold-500 font-serif font-black text-lg md:text-xl mb-6 md:mb-8 flex items-center">
                <span className="w-8 h-[3px] bg-gold-600 mr-4"></span>
                Location
              </h4>
              <p className="mb-6 leading-relaxed font-bold text-sm md:text-base text-gray-200">{ADDRESS}</p>
              <a href={GOOGLE_MAPS_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-gold-500 hover:text-white transition-colors group text-xs md:text-sm font-black uppercase tracking-widest">
                <MapPin className="w-4 h-4 mr-2 group-hover:animate-bounce" /> Open in Google Maps
              </a>
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <h4 className="text-gold-500 font-serif font-black text-lg md:text-xl mb-6 md:mb-8 flex items-center">
                <span className="w-8 h-[3px] bg-gold-600 mr-4"></span>
                Get In Touch
              </h4>
              <div className="space-y-6">
                <a href={`tel:${PHONE_NUMBER}`} className="block group">
                  <span className="text-xs uppercase tracking-widest text-gray-500 font-bold block mb-1">Call Us</span>
                  <span className="text-white group-hover:text-gold-500 transition-colors text-lg md:text-xl font-black">{PHONE_NUMBER}</span>
                </a>
                <a href={`mailto:${EMAIL}`} className="block group">
                  <span className="text-xs uppercase tracking-widest text-gray-500 font-bold block mb-1">Email Us</span>
                  <span className="text-white group-hover:text-gold-500 transition-colors text-base md:text-lg font-bold break-all">{EMAIL}</span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-16 md:mt-20 pt-8 md:pt-10 border-t border-white/10 flex flex-col lg:flex-row justify-between items-center text-xs md:text-sm font-bold text-gold-600 gap-4 md:gap-6 text-center lg:text-left animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            <p className="opacity-100 font-black tracking-wide">&copy; {new Date().getFullYear()} {COMPANY_NAME}. All Rights Reserved.</p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-8">
              <Link to="/terms" className="text-gold-600 hover:text-white transition-colors font-bold uppercase tracking-wider">Terms & Policies</Link>
              <a href={GOOGLE_REVIEWS_LINK} target="_blank" className="flex items-center gap-2 text-gold-600 hover:text-white transition-colors font-bold uppercase tracking-wider">
                <Star className="w-4 h-4 text-gold-600 fill-gold-600" /> Review us on Google
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
